import { noop } from '@kofno/piper';
import { fromEmpty } from 'maybeasy';
import React, {
  createContext,
  ReactNode,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { FileMetaData } from '../../../Edit/CurrentCollectionStep/Details/S3ImageUpload/Types';
import {
  initialContextState,
  LearningCollectionResourceWithScheduledPrograms,
  ScheduledProgram,
} from '../Types';
import { getScheduledDateIdsFromStore } from '../utils/scheduledPrograms';

// Discriminated union for logo change tracking
export type LogoChange =
  | { kind: 'none' } // No change to logo
  | { kind: 'upload'; data: FileMetaData } // User uploaded new logo
  | { kind: 'remove' }; // User took the logo off the collection (DVLP-7971)

interface Props {
  resourceState: LearningCollectionResourceWithScheduledPrograms;
  setResourceState: (
    state:
      | LearningCollectionResourceWithScheduledPrograms
      | ((
          prev: LearningCollectionResourceWithScheduledPrograms,
        ) => LearningCollectionResourceWithScheduledPrograms),
  ) => void;
  logoChange: LogoChange;
  setLogoChange: (change: LogoChange) => void;
  handleLearningCollectionNameChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleProductCollectionNameChange: (sectionIndex: number, newName: string) => void;
  handleProductCollectionDescriptionChange: (sectionIndex: number, newDescription: string) => void;
  handleProductCollectionDisplayTypeChange: (
    sectionIndex: number,
    displayType: 'carousel' | 'grid' | 'list',
  ) => void;
  handleProductCollectionDisplayButtonLabelChange: (
    sectionIndex: number,
    buttonLabel: string,
  ) => void;
  reorderProgramsInSection: (sectionIndex: number, fromIndex: number, toIndex: number) => void;
  moveProgramBetweenSections: (
    sourceSectionIndex: number,
    destSectionIndex: number,
    sourceIndex: number,
    destIndex: number,
  ) => void;
  reorderProductCollections: (fromIndex: number, toIndex: number) => void;
  handleMoveProductCollectionUp: (index: number) => void;
  handleMoveProductCollectionDown: (index: number) => void;
  handleDeleteProductCollection: (index: number) => void;
  // Handler for toggling scheduled program dates - stores directly in resourceState
  handleScheduledProgramToggle: (
    experienceId: number,
    programId: number,
    selected: boolean,
  ) => void;
}

const LearningCollectionContext = createContext<Props>({
  resourceState: initialContextState,
  setResourceState: noop,
  logoChange: { kind: 'none' },
  setLogoChange: noop,
  handleLearningCollectionNameChange: noop,
  handleProductCollectionNameChange: noop,
  handleProductCollectionDescriptionChange: noop,
  handleProductCollectionDisplayTypeChange: noop,
  handleProductCollectionDisplayButtonLabelChange: noop,
  reorderProgramsInSection: noop,
  moveProgramBetweenSections: noop,
  reorderProductCollections: noop,
  handleMoveProductCollectionUp: noop,
  handleMoveProductCollectionDown: noop,
  handleDeleteProductCollection: noop,
  handleScheduledProgramToggle: noop,
});

export const useLearningCollection = () => {
  const context = useContext(LearningCollectionContext);

  if (!context) {
    throw new Error('useLearningCollection must be used within a LearningCollectionProvider');
  }

  return context;
};

// Domain functions for boundary checks
const canMoveUp = (index: number): boolean => index > 0;
const canMoveDown = (index: number, totalSections: number): boolean => index < totalSections - 1;

// Domain function: toggle an id in an array (add if selected, remove if not)
const toggleInArray = (arr: number[], id: number, selected: boolean): number[] =>
  selected ? [...arr, id] : arr.filter((x) => x !== id);

// Domain function: upsert a scheduled program with a transform
const upsertScheduledProgram = (
  programs: ScheduledProgram[],
  experienceId: number,
  transform: (programIds: number[]) => number[],
): ScheduledProgram[] => {
  const existing = programs.find((sp) => sp.id === experienceId);

  if (existing) {
    return programs.map((sp) =>
      sp.id === experienceId ? { ...sp, program_ids: transform(sp.program_ids) } : sp,
    );
  }

  const newProgramIds = transform([]);
  return newProgramIds.length > 0
    ? [...programs, { id: experienceId, program_ids: newProgramIds }]
    : programs;
};

// Domain functions for array manipulation
const moveItemInArray = <T,>(array: T[], fromIndex: number, toIndex: number): T[] => {
  const result = [...array];
  const [removed] = result.splice(fromIndex, 1);
  result.splice(toIndex, 0, removed);
  return result;
};

const swapItemsInArray = <T,>(array: T[], index1: number, index2: number): T[] => {
  const result = [...array];
  [result[index1], result[index2]] = [result[index2], result[index1]];
  return result;
};

export const LearningCollectionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [resourceState, setResourceState] =
    useState<LearningCollectionResourceWithScheduledPrograms>(initialContextState);
  const [logoChange, setLogoChange] = useState<LogoChange>({ kind: 'none' });

  // Handler for toggling scheduled program date selections
  // Note: Validation that programId belongs to experienceId happens in the EditStartDatesModal,
  // which only renders valid dates for that experience from program_instance_options.scheduled
  const handleScheduledProgramToggle = useCallback(
    (experienceId: number, programId: number, selected: boolean) => {
      setResourceState((prev) => ({
        ...prev,
        payload: {
          ...prev.payload,
          scheduled_programs: upsertScheduledProgram(
            prev.payload.scheduled_programs ?? [],
            experienceId,
            (programIds) => toggleInArray(programIds, programId, selected),
          ),
        },
      }));
    },
    [],
  );

  const handleLearningCollectionNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setResourceState((prev) => ({
        ...prev,
        payload: { ...prev.payload, name: event.target.value },
      }));
    },
    [],
  );

  // When a Browse All section is modified (name, description, display, or programs),
  // set browse_all to false to convert it to a regular section. This allows users
  // to add a new Browse All section if needed. The learning_design_state remains
  // 'browse-all' to preserve template origin for UI display.
  const handleProductCollectionNameChange = useCallback((sectionIndex: number, newName: string) => {
    setResourceState((prev) => ({
      ...prev,
      payload: {
        ...prev.payload,
        product_collections: prev.payload.product_collections.map((pc, i) =>
          i === sectionIndex
            ? { ...pc, payload: { ...pc.payload, name: newName, browse_all: false } }
            : pc,
        ),
      },
    }));
  }, []);

  const handleProductCollectionDescriptionChange = useCallback(
    (sectionIndex: number, newDescription: string) => {
      setResourceState((prev) => ({
        ...prev,
        payload: {
          ...prev.payload,
          product_collections: prev.payload.product_collections.map((pc, i) =>
            i === sectionIndex
              ? {
                  ...pc,
                  payload: { ...pc.payload, description: newDescription, browse_all: false },
                }
              : pc,
          ),
        },
      }));
    },
    [],
  );

  const handleProductCollectionDisplayTypeChange = useCallback(
    (sectionIndex: number, displayType: 'carousel' | 'grid' | 'list') => {
      setResourceState((prev) => ({
        ...prev,
        payload: {
          ...prev.payload,
          product_collections: prev.payload.product_collections.map((pc, i) =>
            i === sectionIndex
              ? { ...pc, payload: { ...pc.payload, display_type: displayType, browse_all: false } }
              : pc,
          ),
        },
      }));
    },
    [],
  );

  const handleProductCollectionDisplayButtonLabelChange = useCallback(
    (sectionIndex: number, buttonLabel: string) => {
      const trimmedLabel = buttonLabel.trim();
      setResourceState((prev) => ({
        ...prev,
        payload: {
          ...prev.payload,
          product_collections: prev.payload.product_collections.map((pc, i) =>
            i === sectionIndex
              ? {
                  ...pc,
                  payload: {
                    ...pc.payload,
                    display_button_label: fromEmpty(trimmedLabel),
                    browse_all: false,
                  },
                }
              : pc,
          ),
        },
      }));
    },
    [],
  );

  const reorderProgramsInSection = useCallback(
    (sectionIndex: number, fromIndex: number, toIndex: number) => {
      setResourceState((prev) => {
        const section = prev.payload.product_collections[sectionIndex];
        const reorderedExperiences = moveItemInArray(
          section.payload.experiences,
          fromIndex,
          toIndex,
        );

        return {
          ...prev,
          payload: {
            ...prev.payload,
            product_collections: prev.payload.product_collections.map((pc, i) =>
              i === sectionIndex
                ? {
                    ...pc,
                    payload: {
                      ...pc.payload,
                      experiences: reorderedExperiences,
                      browse_all: false,
                    },
                  }
                : pc,
            ),
          },
        };
      });
    },
    [],
  );

  const moveProgramBetweenSections = useCallback(
    (
      sourceSectionIndex: number,
      destSectionIndex: number,
      sourceIndex: number,
      destIndex: number,
    ) => {
      setResourceState((prev) => {
        const sourceSection = prev.payload.product_collections[sourceSectionIndex];
        const destSection = prev.payload.product_collections[destSectionIndex];

        if (!sourceSection || !destSection) {
          console.warn('Invalid section indices for cross-section move');
          return prev;
        }

        const movedExperienceId = sourceSection.payload.experiences[sourceIndex];
        if (movedExperienceId === undefined) {
          console.warn('Invalid experience index for cross-section move');
          return prev;
        }

        const newSourceExperiences = [...sourceSection.payload.experiences];
        newSourceExperiences.splice(sourceIndex, 1);

        const newDestExperiences = [...destSection.payload.experiences];
        newDestExperiences.splice(destIndex, 0, movedExperienceId);

        return {
          ...prev,
          payload: {
            ...prev.payload,
            product_collections: prev.payload.product_collections.map((pc, i) => {
              if (i === sourceSectionIndex) {
                return {
                  ...pc,
                  payload: {
                    ...pc.payload,
                    experiences: newSourceExperiences,
                    browse_all: false,
                  },
                };
              }
              if (i === destSectionIndex) {
                return {
                  ...pc,
                  payload: {
                    ...pc.payload,
                    experiences: newDestExperiences,
                    browse_all: false,
                  },
                };
              }
              return pc;
            }),
          },
        };
      });
    },
    [],
  );

  const reorderProductCollections = useCallback((fromIndex: number, toIndex: number) => {
    setResourceState((prev) => {
      const collections = moveItemInArray(prev.payload.product_collections, fromIndex, toIndex);

      return {
        ...prev,
        payload: {
          ...prev.payload,
          product_collections: collections,
        },
      };
    });
  }, []);

  const handleMoveProductCollectionUp = useCallback((index: number) => {
    setResourceState((prev) => {
      if (!canMoveUp(index)) return prev;

      const collections = swapItemsInArray(prev.payload.product_collections, index - 1, index);

      return {
        ...prev,
        payload: {
          ...prev.payload,
          product_collections: collections,
        },
      };
    });
  }, []);

  const handleMoveProductCollectionDown = useCallback((index: number) => {
    setResourceState((prev) => {
      const totalSections = prev.payload.product_collections.length;
      if (!canMoveDown(index, totalSections)) return prev;

      const collections = swapItemsInArray(prev.payload.product_collections, index, index + 1);

      return {
        ...prev,
        payload: {
          ...prev.payload,
          product_collections: collections,
        },
      };
    });
  }, []);

  const handleDeleteProductCollection = useCallback((index: number) => {
    setResourceState((prev) => {
      const collections = [...prev.payload.product_collections];
      // Remove section at index
      collections.splice(index, 1);

      return {
        ...prev,
        payload: {
          ...prev.payload,
          product_collections: collections,
        },
      };
    });
  }, []);

  // Auto-sync scheduled_programs when product_collections change
  // This ensures all scheduled experiences have entries with all dates selected by default
  // Uses "adjust during render" pattern instead of an effect to avoid event-handler/derived-state lint issues
  const prevCollectionsRef = useRef(resourceState.payload.product_collections);

  if (prevCollectionsRef.current !== resourceState.payload.product_collections) {
    prevCollectionsRef.current = resourceState.payload.product_collections;

    // Note: scheduled_programs is intentionally NOT in the dependency array to avoid
    // infinite loops (this effect updates scheduled_programs). The functional updater
    // pattern (setResourceState(prev => ...)) ensures we always append to the latest
    // scheduled_programs even if it changed since the effect started.

    // Get all experience IDs from all sections
    const allExperienceIds = resourceState.payload.product_collections.flatMap(
      (pc) => pc.payload.experiences,
    );

    // Get existing scheduled_programs
    const existingScheduledPrograms = resourceState.payload.scheduled_programs ?? [];
    const existingIds = new Set(existingScheduledPrograms.map((sp) => sp.id));

    // Find experiences that need entries (not already in scheduled_programs)
    const missingIds = allExperienceIds.filter((id) => !existingIds.has(id));

    if (missingIds.length > 0) {
      // Create entries for missing experiences (only those with scheduled dates)
      const newEntries: ScheduledProgram[] = missingIds
        .map((experienceId) => {
          const dateIds = getScheduledDateIdsFromStore(experienceId);
          if (dateIds.length === 0) {
            return null; // Not a scheduled experience
          }
          return {
            id: experienceId,
            program_ids: dateIds, // All dates selected by default
          };
        })
        .filter((entry): entry is ScheduledProgram => entry !== null);

      if (newEntries.length > 0) {
        // Schedule state update for next render to avoid updating during render
        setResourceState((prev) => ({
          ...prev,
          payload: {
            ...prev.payload,
            scheduled_programs: [...(prev.payload.scheduled_programs ?? []), ...newEntries],
          },
        }));
      }
    }
  }

  const contextValue = useMemo(
    () => ({
      resourceState,
      setResourceState,
      logoChange,
      setLogoChange,
      handleLearningCollectionNameChange,
      handleProductCollectionNameChange,
      handleProductCollectionDescriptionChange,
      handleProductCollectionDisplayTypeChange,
      handleProductCollectionDisplayButtonLabelChange,
      reorderProgramsInSection,
      moveProgramBetweenSections,
      reorderProductCollections,
      handleMoveProductCollectionUp,
      handleMoveProductCollectionDown,
      handleDeleteProductCollection,
      handleScheduledProgramToggle,
    }),
    [
      resourceState,
      logoChange,
      handleLearningCollectionNameChange,
      handleProductCollectionNameChange,
      handleProductCollectionDescriptionChange,
      handleProductCollectionDisplayTypeChange,
      handleProductCollectionDisplayButtonLabelChange,
      reorderProgramsInSection,
      moveProgramBetweenSections,
      reorderProductCollections,
      handleMoveProductCollectionUp,
      handleMoveProductCollectionDown,
      handleDeleteProductCollection,
      handleScheduledProgramToggle,
    ],
  );

  return (
    <LearningCollectionContext.Provider value={contextValue}>
      {children}
    </LearningCollectionContext.Provider>
  );
};
